<template>
  <v-dialog
    v-model="show"
    max-width="400"
    @click:outside="handleClose"
    @keydown.esc="handleClose"
  >
    <v-card>
      <v-card-title class="pr-3 pb-0">
        <v-spacer></v-spacer>

        <v-icon @click="handleClose" style="cursor: pointer"
          >mdi-close</v-icon
        ></v-card-title
      >

      <v-card-text class="pt-0 px-2">
        <!-- VIDEO -->
        <v-card-text v-if="showVideo">
          <!-- External video (iframe) -->
          <iframe
            v-if="isExternalVideo"
            id="video"
            ref="video"
            :src="options.video + '&autoplay=1'"
            width="100%"
            height="460"
            title=" "
            frameborder="0"
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            allowfullscreen
          ></iframe>
          <!-- Local video file -->
          <video
            v-else
            ref="localVideo"
            width="100%"
            height="460"
            controls
            autoplay
            loop
          >
            <source :src="localVideoSrc" :type="videoMimeType" />
            Your browser does not support the video tag.
          </video>
        </v-card-text>
        <!-- IMAGE -->
        <v-card-text v-if="options.popUpImg">
          <v-img
            contain
            :lazy-src="require(`@/assets/apple-style/${options.popUpImg}`)"
            :src="require(`@/assets/apple-style/${options.popUpImg}`)"
          />
        </v-card-text>
        <!-- TEXT -->
        <!-- if not inside cert page and doesn't have cert text, but has regular text -->
        <v-card-text
          v-if="options.text && !showCertText"
          v-html="options.text"
          class="text-body-1 pt-1"
        ></v-card-text>
        <!-- if inside certification page and has cert text -->
        <template v-if="showCertText">
          <v-card-text
            class="text-h6 font-weight-bold py-0"
            v-if="options.certificationText[certPage].title1"
            v-text="options.certificationText[certPage].title1"
          />
          <v-card-text
            class="pt-0"
            v-if="options.certificationText[certPage].title2"
            v-text="options.certificationText[certPage].title2"
          />
          <v-card-text
            class="font-weight-bold py-0"
            v-if="options.certificationText[certPage].subtitle1"
            v-text="options.certificationText[certPage].subtitle1"
          />
          <v-card-text
            class="font-weight-bold py-0"
            v-if="options.certificationText[certPage].subtitle2"
            v-text="options.certificationText[certPage].subtitle2"
          />
          <v-card-text
            v-if="options.certificationText[certPage].htmlText1"
            v-html="options.certificationText[certPage].htmlText1"
          />
          <v-card-text
            class="font-weight-bold pb-0"
            v-if="options.certificationText[certPage].subtitle3"
            v-text="options.certificationText[certPage].subtitle3"
          />
          <v-card-text
            class="font-weight-bold py-0"
            v-if="options.certificationText[certPage].subtitle4"
            v-text="options.certificationText[certPage].subtitle4"
          />
          <v-card-text
            v-if="options.certificationText[certPage].htmlText2"
            v-html="options.certificationText[certPage].htmlText2"
          />
        </template>
        <!-- BUTTON -->
        <v-card-text v-if="action && options.popUpBtnText">
          <v-btn
            rounded
            color="primary"
            class="white--text"
            @click="handleBtnClick"
            >{{ options.popUpBtnText }}</v-btn
          ></v-card-text
        >
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
  /**
   * PopUp Component
   *
   * Supports both external video links and local video files:
   * - External videos: Use full URLs (e.g., 'https://player.vimeo.com/video/123456')
   * - Local videos: Use filename only (e.g., '1-laserbeamer-popup.mp4')
   *   Local videos are automatically loaded from @/assets/apple-style/ folder
   *   Supported formats: mp4, webm, ogg, mov, avi
   */
  export default {
    props: {
      show: {
        type: Boolean,
        default: false
      },
      options: {
        type: Object,
        default: () => ({
          img: '',
          hasAction: false,
          video: '',
          popUpImg: '',
          text: '',
          popUpBtnText: '',
          certificationText: ''
        })
      },
      action: {
        type: Boolean,
        default: true
      },
      certPage: { type: String, default: '' }
    },
    computed: {
      showCertText() {
        // if is inside certification page and this img has certification text
        return !!this.certPage && !!this.options.certificationText
      },
      showVideo() {
        // do not show vide if no video or it is inside certification page if it is EXPLICITLY set to not show video on cert page
        return (
          !!this.options.video &&
          (!this.options.isCertPage ||
            this.options.showVideoOnCertPage !== false)
        )
      },
      isExternalVideo() {
        // Check if video is an external link (contains http/https or player.vimeo.com)
        return (
          this.options.video &&
          (this.options.video.startsWith('http://') ||
            this.options.video.startsWith('https://') ||
            this.options.video.includes('player.vimeo.com'))
        )
      },
      localVideoSrc() {
        // For local videos, require the video from assets folder
        if (!this.isExternalVideo && this.options.video) {
          try {
            return require(`@/assets/apple-style/${this.options.video}`)
          } catch (error) {
            console.warn(`Video file not found: ${this.options.video}`)
            return ''
          }
        }
        return ''
      },
      videoMimeType() {
        // Determine MIME type based on file extension
        if (!this.options.video) return 'video/mp4'

        const extension = this.options.video.split('.').pop().toLowerCase()
        const mimeTypes = {
          mp4: 'video/mp4',
          webm: 'video/webm',
          ogg: 'video/ogg',
          mov: 'video/quicktime',
          avi: 'video/x-msvideo'
        }
        return mimeTypes[extension] || 'video/mp4'
      }
    },
    methods: {
      handleClose() {
        this.$emit('close', false)
        // Reset external video iframe
        if (this.isExternalVideo && this.$refs.video) {
          this.$refs.video.src = ''
          this.$refs.video.src = this.options.video + '&autoplay=1'
        }
        // Pause local video
        if (!this.isExternalVideo && this.$refs.localVideo) {
          this.$refs.localVideo.pause()
          this.$refs.localVideo.currentTime = 0
        }
      },
      handleBtnClick() {
        this.$emit('btn-click')
      }
    }
  }
</script>

<style lang="scss" scoped></style>
